import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { Sport } from '__generated__/globalTypes';
import type { PathParams } from 'lib/routing/generateSportPath';
import {
  generateSportPath,
  matchAnySportPath,
} from 'lib/routing/generateSportPath';
import { sportFromPath } from 'lib/routing/sportFromPath';

import { SportContext, sportAnalyticTag } from '.';

interface Props {
  children: ReactNode;
  sport?: Sport;
}

export const SportProvider = ({ children, sport: defaultSport }: Props) => {
  const { pathname } = useLocation();
  const [sport, setSport] = useState<Sport>(
    defaultSport || sportFromPath(pathname)
  );

  const value = useMemo(
    () => ({
      sport,
      sportAnalyticsTag: sportAnalyticTag[sport],
      setSport,
      // wrap the `generateSportPath` to handle the default sport
      generateSportContextPath: <Path extends string>(
        path: Path,
        {
          params,
          sport: s = sport,
        }: {
          params?: PathParams<Path>;
          sport?: Sport;
        } = {}
      ) => generateSportPath(path, { params, sport: s }),
      // eslint-disable-next-line @typescript-eslint/no-shadow
      matchAnySportPath: (pattern: string, pathname: string, s?: Sport) =>
        matchAnySportPath(pattern, pathname, s || sport),
    }),
    [sport]
  );

  return (
    <SportContext.Provider value={value}>{children}</SportContext.Provider>
  );
};
